/**
 * /slides — 사용자 슬라이드 목록.
 *
 * 빈 상태: 큰 "슬라이드 제작 시작" CTA.
 * 보유 시: 카드 그리드 (제목 / 페이지 수 / 즐겨찾기). 클릭 시 /slides/:id 진입.
 */
import { SidebarOpenerButton } from '@/components/layout/SidebarOpenerButton';
import { SlideCardMenu } from '@/components/slides/SlideCardMenu';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Plus, Presentation, Star } from 'lucide-react';
import { useState } from 'react';

export function SlidesIndex() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['slides', 'list'],
    queryFn: api.slidesList,
    staleTime: 5_000,
  });

  const createMut = useMutation({
    mutationFn: () => api.slideCreate({}),
    onSuccess: (slide) => {
      qc.invalidateQueries({ queryKey: ['slides', 'list'] });
      navigate({ to: '/slides/$slideId', params: { slideId: slide.id } });
    },
    onError: (e) => setError(e instanceof Error ? e.message : '슬라이드 생성 실패'),
  });

  const slides = listQuery.data?.slides ?? [];
  const isEmpty = !listQuery.isLoading && slides.length === 0;

  return (
    <div className="flex flex-col h-full">
      <header className="flex items-center gap-2 px-6 py-3 border-b border-border bg-background sticky top-0 z-10">
        <SidebarOpenerButton />
        <h1 className="text-base font-semibold">슬라이드</h1>
        <div className="flex-1" />
        {!isEmpty && (
          <Button size="sm" onClick={() => createMut.mutate()} disabled={createMut.isPending}>
            <Plus className="size-4" />새 슬라이드
          </Button>
        )}
      </header>

      <div className="flex-1 overflow-y-auto">
        {error && (
          <div className="mx-6 mt-4 px-4 py-3 rounded-md bg-destructive/10 text-destructive text-sm">
            {error}
          </div>
        )}

        {listQuery.isLoading ? (
          <div className="p-6 text-sm text-foreground-muted">불러오는 중…</div>
        ) : isEmpty ? (
          <EmptyState onCreate={() => createMut.mutate()} isCreating={createMut.isPending} />
        ) : (
          <div className="grid gap-4 p-6 grid-cols-[repeat(auto-fill,minmax(260px,1fr))]">
            {slides.map((s) => (
              <div
                key={s.id}
                className="group relative rounded-lg border border-border bg-surface-muted/30 hover:bg-surface-muted/60 transition-colors overflow-hidden"
              >
                <Link to="/slides/$slideId" params={{ slideId: s.id }} className="block">
                  <div className="aspect-video bg-surface-muted/60 grid place-items-center text-foreground-muted">
                    <Presentation className="size-8 opacity-30 group-hover:opacity-60 transition-opacity" />
                  </div>
                  <div className="p-3">
                    <div className="flex items-start gap-2">
                      <h3 className="text-sm font-medium flex-1 truncate">{s.title}</h3>
                      {s.isFavorite && <Star className="size-3.5 fill-current text-yellow-500" />}
                    </div>
                    <p className="text-xs text-foreground-muted mt-1 line-clamp-1">
                      {s.preview ?? `${s.pageCount}장`}
                    </p>
                  </div>
                </Link>
                <div className="absolute top-2 right-2 z-10">
                  <SlideCardMenu slide={s} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState({
  onCreate,
  isCreating,
}: {
  onCreate: () => void;
  isCreating: boolean;
}) {
  return (
    <div className="h-full grid place-items-center px-6 py-16">
      <div className="max-w-md text-center">
        <Presentation className="size-12 mx-auto text-foreground-muted opacity-40" />
        <h2 className="mt-6 text-xl font-semibold">아직 만든 슬라이드가 없어요</h2>
        <p className="mt-3 text-sm text-foreground-muted leading-relaxed">
          좌측 채팅으로 LLM 과 대화하면서 우측에서 1920×1080 슬라이드가 실시간으로 만들어집니다.
          <br />첫 메시지에 발표 주제·청중·핵심 메시지 등을 풍부하게 적을수록 좋은 결과가 나와요.
        </p>
        <Button size="lg" className="mt-8" onClick={onCreate} disabled={isCreating}>
          <Plus className="size-5" />
          {isCreating ? '만드는 중…' : '슬라이드 제작 시작'}
        </Button>
      </div>
    </div>
  );
}
